import React from 'react';
import { ScoreSlider, SliderRenderProps } from './ScoreSlider';
import { cn } from '../primitives';

export interface ScoreFieldLabel {
  value: number;
  name: string | null;
  color?: string | null;
}

export interface ScoreField {
  id: string;
  name: string | null;
  icon: string | null;
  minValue: number | null;
  maxValue: number | null;
  stepValue: number | null;
  units: string | null;
  /** Field score (0-100) that determines the color (0=red, 100=green) */
  score: number | null;
  labels?: ScoreFieldLabel[];
}

export interface ScoreWidgetProps {
  /** Widget ID */
  id: string;
  /** Widget name/title */
  name: string;
  /** List of score fields to display */
  scoreFields: ScoreField[];
  /** Map of field ID to current value (null means not selected) */
  fieldValues: Record<string, number | null>;
  /** Callback when a field value is released */
  onFieldValueChange: (fieldId: string, value: number) => void;
  /** Whether the widget is loading */
  isLoading?: boolean;
  /** Custom class name */
  className?: string;
  /** Custom render function for sliders (React Native support) */
  renderSlider?: (props: SliderRenderProps) => React.ReactNode;
  /** Custom render function for the card container (React Native support) */
  renderCard?: (props: { children: React.ReactNode; title: string }) => React.ReactNode;
}

/**
 * ScoreWidget - Displays a collection of score sliders in a card
 *
 * Designed to be cross-platform compatible:
 * - Web: Uses default HTML elements
 * - React Native: Pass renderCard and renderSlider props
 */
export function ScoreWidget({
  id,
  name,
  scoreFields,
  fieldValues,
  onFieldValueChange,
  isLoading = false,
  className,
  renderSlider,
  renderCard,
}: ScoreWidgetProps) {
  const content = (
    <>
      {scoreFields.map((field) => {
        // Skip fields without proper min/max values
        if (field.minValue === null || field.maxValue === null) {
          return null;
        }

        // Get the current value - preserve null for unselected state
        // If field.id is not in fieldValues, treat as null (unselected)
        const currentValue: number | null = Object.prototype.hasOwnProperty.call(fieldValues, field.id)
          ? (fieldValues[field.id] ?? null)
          : null;

        return (
          <ScoreSlider
            key={field.id}
            fieldId={field.id}
            icon={field.icon}
            name={field.name || 'Unnamed Field'}
            min={field.minValue}
            max={field.maxValue}
            step={field.stepValue || 1}
            currentValue={currentValue}
            units={field.units}
            score={field.score ?? 50}
            labels={field.labels}
            onRelease={(value) => onFieldValueChange(field.id, value)}
            disabled={isLoading}
            renderSlider={renderSlider}
          />
        );
      })}
      {scoreFields.length === 0 && (
        <div className="score-widget__empty">
          No score fields configured
        </div>
      )}
    </>
  );

  // If custom card renderer is provided (React Native), use it
  if (renderCard) {
    return renderCard({ children: content, title: name });
  }

  return (
    <div className={cn('card shadow-sm diary-category-card mb-3', className)} data-widget-id={id}>
      <div className="card-header">
        <h5 className="card-title mb-0">{name}</h5>
      </div>
      <div className="card-body" style={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
        {content}
      </div>
    </div>
  );
}

export default ScoreWidget;
